import React, { memo, useState, ReactNode } from "react";
import { IChannel } from "@Interfaces/Entities/Channels";
import {
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListItemSecondaryAction,
    Menu,
    MenuItem,
    Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useDispatch, useSelector } from "react-redux";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import IosShareIcon from "@mui/icons-material/IosShare";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import SettingsIcon from "@mui/icons-material/Settings";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
    AuthSelectors,
    ChannelActions,
    NotificationActions,
    RootStateType,
} from "@Store";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { DialogComponents } from "@Interfaces/Entities/Dialogs";
import { useTranslation } from "react-i18next";
import { getNextActiveState } from "@Utils/channelActive";

type IAdminType = { email: string; username: string; _id: string };
type ChannelMenuAction = {
    key: string;
    label: ReactNode;
    icon: ReactNode;
    onClick: () => void | Promise<void>;
    destructive?: boolean;
};

const MemoizedChannelMenu: React.FunctionComponent<{
    channel: IChannel;
    extraActions?: ReactNode;
    renderInList?: boolean;
}> = ({ channel, extraActions, renderInList = true }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();
    const theme = useTheme();
    const isMobileMenu = useMediaQuery(theme.breakpoints.down("md"));

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const user = useSelector((state: RootStateType) => state.auth.user);

    const { admins } = channel;
    const isOwner = admins?.some((admin: IAdminType) => admin._id === user.id);
    const isAuthUser = AuthSelectors.selectIsUserAuthorized();
    const isSubChannel = Boolean(channel.parent_channel_id);
    const hasSubChannels =
        Boolean(channel.has_children) ||
        Boolean(channel.sub_channels && channel.sub_channels.length > 0);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const runAction = async (action: ChannelMenuAction) => {
        handleClose();
        await action.onClick();
    };

    const handleDeleteChannel = () => {
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.DeleteChannelDialog,
                props: {
                    ...channel,
                },
            })
        );
    };

    const handleSettingsChannel = () => {
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.ChannelSettingsDialog,
                props: {
                    title: t("dialogs.channel_settings.dialog_title"),
                    channel,
                    maxWidth: "md",
                },
            })
        );
    };

    const handleReorderComponents = () => {
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.ComponentReorderModal,
                props: {
                    channelId: channel._id,
                },
            })
        );
    };

    const handleExportQrChannel = () => {
        dispatch(
            ChannelActions.createSharedLink({
                channel_id: channel._id,
                channel_title: channel.title,
                channel_description: channel.description,
                dialog_id: DialogComponents.ExportToDialog,
            })
        );
    };

    const handleOpenSubChannelDialog = () => {
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.CreateChannelDialog,
                props: {
                    parentChannel: channel,
                },
            })
        );
    };

    const handleCloneEvent = () => {
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.CloneChannelDialog,
                props: {
                    ...channel,
                },
            })
        );
    };

    const handleShareChannel = () => {
        dispatch(
            ChannelActions.createSharedLink({
                channel_id: channel._id,
                channel_title: channel.title,
                dialog_id: DialogComponents.SharedLinkDialog,
            })
        );
    };

    const handleTogglePublish = async () => {
        const nextActiveState = getNextActiveState(channel.active);
        try {
            await dispatch(
                ChannelActions.updateChannel(channel, {
                    active: nextActiveState,
                })
            );

            dispatch(
                NotificationActions.notification({
                    snackbarOpen: true,
                    snackbarType: "success",
                    snackbarMessage: nextActiveState
                        ? t("dialogs.channel.publish_success", {
                              defaultValue: "Event published.",
                          })
                        : t("dialogs.channel.unpublish_success", {
                              defaultValue: "Event unpublished.",
                          }),
                })
            );
        } catch {
            dispatch(
                NotificationActions.notification({
                    snackbarOpen: true,
                    snackbarType: "error",
                    snackbarMessage: t("dialogs.channel.publish_error", {
                        defaultValue: "Could not update the event status.",
                    }),
                })
            );
        }
    };

    const handleCloseEvent = () => {
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.CloseEventConfirmationDialog,
                props: {
                    ...channel,
                    maxWidth: "sm",
                },
            })
        );
    };

    const handleReactivateEvent = async () => {
        await dispatch(
            ChannelActions.updateChannel(channel, {
                closed: false,
                active: true,
            })
        );
    };

    const publishLabel =
        channel.active === false
            ? t("dialogs.channel.publish", {
                  defaultValue: "Publish",
              })
            : t("dialogs.channel.unpublish", {
                  defaultValue: "Unpublish",
              });

    const publishIcon =
        channel.active === false ? <VisibilityIcon /> : <VisibilityOffIcon />;

    const getOwnerActions = (): Array<ChannelMenuAction | "divider"> => {
        if (isSubChannel) {
            return [
                !channel.closed && {
                    key: "reorder",
                    label: t("dialogs.channel.reorder_components", {
                        defaultValue: "Edit/Re-order",
                    }),
                    icon: <DragIndicatorIcon />,
                    onClick: handleReorderComponents,
                },
                {
                    key: "clone",
                    label: t("dialogs.channel.clone_event"),
                    icon: <ContentCopyIcon />,
                    onClick: handleCloneEvent,
                },
                !channel.closed && {
                    key: "settings",
                    label: t("dialogs.channel.settings"),
                    icon: <SettingsIcon />,
                    onClick: handleSettingsChannel,
                },
                !channel.closed && {
                    key: "publish",
                    label: publishLabel,
                    icon: publishIcon,
                    onClick: handleTogglePublish,
                },
                "divider",
                !channel.closed
                    ? {
                          key: "close",
                          label: t("dialogs.channel.close_event", {
                              defaultValue: "Close Event",
                          }),
                          icon: <CloseIcon />,
                          onClick: handleCloseEvent,
                      }
                    : {
                          key: "reactivate",
                          label: t("dialogs.channel.reactivate_event", {
                              defaultValue: "Re-Open Event",
                          }),
                          icon: <LockOpenIcon />,
                          onClick: handleReactivateEvent,
                      },
                {
                    key: "delete",
                    label: t("dialogs.channel.delete"),
                    icon: <DeleteOutlineIcon />,
                    onClick: handleDeleteChannel,
                    destructive: true,
                },
            ].filter(Boolean) as Array<ChannelMenuAction | "divider">;
        }

        const showManageSection = !channel.closed;
        const showEditReorder = !channel.closed && !hasSubChannels;
        const showTopSection = showManageSection || showEditReorder;
        const showCloseAction = !hasSubChannels;

        return [
            showManageSection && {
                key: "add-sub-channel",
                label: t("dialogs.channel.add_sub_channel", {
                    defaultValue: "Add Sub-channel",
                }),
                icon: <AddCircleOutlineIcon />,
                onClick: handleOpenSubChannelDialog,
            },
            showEditReorder && {
                key: "reorder",
                label: t("dialogs.channel.reorder_components", {
                    defaultValue: "Edit/Re-order",
                }),
                icon: <DragIndicatorIcon />,
                onClick: handleReorderComponents,
            },
            showTopSection && "divider",
            {
                key: "share",
                label: t("dialogs.channel.shared_event_channel"),
                icon: <IosShareIcon />,
                onClick: handleShareChannel,
            },
            {
                key: "qr",
                label: t("dialogs.channel.export_to_qr"),
                icon: <QrCode2Icon />,
                onClick: handleExportQrChannel,
            },
            "divider",
            !channel.closed && {
                key: "settings",
                label: t("dialogs.channel.settings"),
                icon: <SettingsIcon />,
                onClick: handleSettingsChannel,
            },
            {
                key: "clone",
                label: t("dialogs.channel.clone_event"),
                icon: <ContentCopyIcon />,
                onClick: handleCloneEvent,
            },
            showCloseAction && "divider",
            showCloseAction &&
                (!channel.closed
                    ? {
                          key: "close",
                          label: t("dialogs.channel.close_event", {
                              defaultValue: "Close Event",
                          }),
                          icon: <CloseIcon />,
                          onClick: handleCloseEvent,
                      }
                    : {
                          key: "reactivate",
                          label: t("dialogs.channel.reactivate_event", {
                              defaultValue: "Re-Open Event",
                          }),
                          icon: <LockOpenIcon />,
                          onClick: handleReactivateEvent,
                      }),
            {
                key: "delete",
                label: t("dialogs.channel.delete"),
                icon: <DeleteOutlineIcon />,
                onClick: handleDeleteChannel,
                destructive: true,
            },
        ].filter(Boolean) as Array<ChannelMenuAction | "divider">;
    };

    // Only the owner (admin) ever sees a channel in their dashboard; there is no
    // non-owner "member" role any more, so non-owners get no menu at all.
    const actions = getOwnerActions();

    const ChannelMenuContent = () => (
        <span>
            {actions.map((action, index) =>
                action === "divider" ? (
                    <Divider key={`divider-${index}`} />
                ) : (
                    <MenuItem
                        key={action.key}
                        onClick={() => runAction(action)}
                        sx={
                            action.destructive
                                ? {
                                      color: "error.main",
                                      fontWeight: "medium",
                                  }
                                : undefined
                        }
                    >
                        {action.label}
                    </MenuItem>
                )
            )}
        </span>
    );

    const MobileMenuContent = () => (
        <Box
            role="presentation"
            sx={{
                pt: 1,
                pb: "calc(env(safe-area-inset-bottom) + 8px)",
            }}
        >
            <Box
                sx={{
                    width: 36,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: "grey.300",
                    mx: "auto",
                    mb: 1,
                }}
            />
            <Box
                sx={{
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 700,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        pr: 1,
                    }}
                >
                    {channel.title}
                </Typography>
                <IconButton
                    aria-label={t("dialogs.close", {
                        defaultValue: "Close",
                    })}
                    onClick={handleClose}
                    edge="end"
                    size="large"
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <List disablePadding>
                {actions.map((action, index) =>
                    action === "divider" ? (
                        <Divider key={`mobile-divider-${index}`} />
                    ) : (
                        <ListItemButton
                            key={action.key}
                            onClick={(event) => {
                                event.stopPropagation();
                                runAction(action);
                            }}
                            sx={{
                                minHeight: 56,
                                px: 2.5,
                                color: action.destructive
                                    ? "error.main"
                                    : "text.primary",
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 40,
                                    color: "inherit",
                                }}
                            >
                                {action.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={action.label}
                                primaryTypographyProps={{
                                    fontWeight: action.destructive ? 600 : 500,
                                }}
                            />
                        </ListItemButton>
                    )
                )}
            </List>
        </Box>
    );

    if (!isAuthUser || !isOwner) {
        return null;
    }

    const MenuTrigger = (
        <IconButton
            edge="end"
            aria-label={t("dialogs.channel.options_aria_label")}
            aria-controls="channel-options-menu"
            aria-haspopup={isMobileMenu ? "dialog" : "menu"}
            onClick={handleClick}
            size="large"
        >
            <Box sx={{ display: { md: "none" } }}>
                <MoreHorizIcon />
            </Box>
            <Box sx={{ display: { xs: "none", md: "block" } }}>
                <MoreVertIcon />
            </Box>
        </IconButton>
    );

    return (
        <>
            {isMobileMenu ? (
                <Drawer
                    anchor="bottom"
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    PaperProps={{
                        sx: {
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                            maxHeight: "85vh",
                        },
                    }}
                >
                    <MobileMenuContent />
                </Drawer>
            ) : (
                <Menu
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                >
                    <ChannelMenuContent />
                </Menu>
            )}
            {renderInList ? (
                <ListItemSecondaryAction>
                    {extraActions}
                    {MenuTrigger}
                </ListItemSecondaryAction>
            ) : (
                <Box sx={{ display: "inline-flex" }}>
                    {extraActions}
                    <Box sx={{ ml: 0.5 }}>{MenuTrigger}</Box>
                </Box>
            )}
        </>
    );
};

export const ChannelMenu = memo(MemoizedChannelMenu);
